import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'

import { TypeItemTrustedBy } from '../../../lib/types'
import { TrustedByItem } from '../../item/trusted-by'
import { BackgroundColor } from '../../../lib/types/background'
import { getGradientBgClassname } from './theme'
import { PlayPauseButton } from '../../play-pause'
import { useIsSectionThemeDark } from '../../../contexts/section-theme-context'
import { usePageTheme } from '../../../contexts/page-theme-context'

import styles from './image-scroller.module.scss'

const DEFAULT_LOGO_WIDTH = 130
const DEFAULT_LOGO_HORIZONTAL_SPACING = 24
const SCROLL_SPEED = 30 // pixels per second

const splitIntoRows = (list: TypeItemTrustedBy[], numberOfRows: number) => {
  const rows = []
  const itemsPerRow = Math.ceil(list.length / numberOfRows)

  for (let i = 0; i < numberOfRows; i++) {
    const row = list.slice(i * itemsPerRow, i * itemsPerRow + itemsPerRow)
    rows.push(row)
  }

  return rows
}

export const ImageScroller: React.FC<{
  list: TypeItemTrustedBy[]
  numberOfRows: number
  background?: BackgroundColor
}> = (props) => {
  const { list = [], numberOfRows = 2, background } = props
  const [isPaused, setIsPaused] = useState(false)
  const { color } = usePageTheme()
  const isDarkTheme = useIsSectionThemeDark()

  const itemWidth = DEFAULT_LOGO_WIDTH

  const rows = splitIntoRows(list, numberOfRows)

  return (
    <div className={styles.imageScrollerContainer}>
      <div>
        {rows.map((row, i) => {
          return (
            <div
              key={i}
              style={
                {
                  // For adding a visual break between rows
                  '--imageScrollerStartOffset': i % 2 === 0 ? '0' : `${Math.ceil(itemWidth / 2)}px`,
                } as React.CSSProperties
              }
            >
              <ImageScrollerRow
                list={row}
                itemWidth={itemWidth}
                isPaused={isPaused}
                backgroundColor={getGradientBgClassname(background, color === 'convertTo2024')}
              />
            </div>
          )
        })}
      </div>
      <PlayPauseButton
        onClick={() => setIsPaused((prev) => !prev)}
        state={isPaused ? 'paused' : 'playing'}
        color={isDarkTheme ? 'neutral1' : 'neutral60'}
      />
    </div>
  )
}

const ImageScrollerRow: React.FC<{
  list: TypeItemTrustedBy[]
  itemWidth: number
  isPaused: boolean
  backgroundColor?: string
}> = (props) => {
  const { list = [], itemWidth, isPaused, backgroundColor } = props

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [enableAnimation, setEnableAnimation] = useState(false)

  // Get theme values for simple fallback
  const isDark = useIsSectionThemeDark()

  // Simple theme-based fallback colors
  const fallbackColor = isDark ? '#0E073B' : '#ffffff'

  useEffect(() => {
    /*
      This soltuion relies heavily on widths with set values.  If the container holding the
      image roll changes, we need to change css values (width and animation timing) for the image roll.
    */
    const scrollContainer = scrollContainerRef.current
    const supportsResizeObserver = 'ResizeObserver' in window
    if (!scrollContainer || !supportsResizeObserver) {
      return
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect
        setContainerWidth(width)
      }
    })

    observer.observe(scrollContainer)
    return () => observer.unobserve(scrollContainer)
  }, [])

  useEffect(() => {
    /*
      There's a weird bug in Safari where animations will not respect the css @keyframes property
      timing.  For this particular situation adding the animation property with a slight delay fixes
      the problem.
    */
    setTimeout(() => setEnableAnimation(true), 100)
  }, [])

  // Flag that indicates the ImageScrollerRow is shorter than the container.
  const isTooShort = list.length * (itemWidth + DEFAULT_LOGO_HORIZONTAL_SPACING) < containerWidth

  const imagesList = list.map((item) => {
    return (
      <div
        className={styles.imageScrollerLogoContainer}
        key={item.sys.id}
        style={
          {
            '--imageScrollerImageWidth': `${itemWidth}px`,
          } as React.CSSProperties
        }
      >
        <TrustedByItem structure={item} isScrollable disableResponsive />
      </div>
    )
  })

  const imageRoll = isTooShort ? (
    <div
      className={styles.imageScrollerShortContainer}
      style={
        {
          '--imageScrollerMinRollSize': `${containerWidth}px`,
        } as React.CSSProperties
      }
    >
      {imagesList}
    </div>
  ) : (
    imagesList
  )

  return (
    <div
      className={cn(styles.imageScroller)}
      ref={scrollContainerRef}
      style={
        {
          '--imageScrollerRollLength': `-${Math.max(
            containerWidth,
            (itemWidth + DEFAULT_LOGO_HORIZONTAL_SPACING) * list.length
          )}px`,
          '--imageScrollerSpeed': `${Math.ceil(
            Math.max(containerWidth, (itemWidth + DEFAULT_LOGO_HORIZONTAL_SPACING) * list.length) / SCROLL_SPEED
          )}s`,
          '--imageScrollerLogoHorizontalSpacing': `${DEFAULT_LOGO_HORIZONTAL_SPACING}px`,
        } as React.CSSProperties
      }
    >
      <div
        className={cn(styles.imageScrollerLeftFade, backgroundColor)}
        style={
          // Only add inline fallback when no CSS class is available
          !backgroundColor && fallbackColor // fallbackColor is used here
            ? { background: `linear-gradient(to right, ${fallbackColor}, transparent)` }
            : undefined
        }
      />
      <div
        className={cn(styles.imageScrollerRightFade, backgroundColor)}
        style={
          // Only add inline fallback when no CSS class is available
          !backgroundColor && fallbackColor // fallbackColor is used here
            ? { background: `linear-gradient(to left, ${fallbackColor}, transparent)` }
            : undefined
        }
      />

      <div
        className={cn(styles.imageScrollerRoll, {
          [styles.imageScrollerRollActive]: enableAnimation,
          [styles.imageScrollerRollPaused]: isPaused,
        })}
        style={{
          width: isTooShort ? containerWidth * 2 : itemWidth * list.length * 2,
        }}
      >
        {/* Show the same row twice for wrapping effect.  When end of the first
            row appears on screen it is combined with the start of the next row.
            This gives the illusion that the row is wrapping */}
        {imageRoll}
        {imageRoll}
      </div>
    </div>
  )
}
